import { Accordion, AccordionItem, Box, Icon, IconButton, Tooltip, Typography } from "@husqvarna/ui-core";
import ProjectOverview from "../../../models/ProjectOverview";
import DashboardItem from "../../../models/DashboardItem";
import LatestUpdateText from "../LatestUpdateText";
import ProjectStatusTag from "../ProjectStatusTag";
import { useDroppable } from "@dnd-kit/core";
import clsx from "clsx";
import { useIntl } from "react-intl";
import ConfirmDelete from "../../../../shared/components/ConfirmDelete/ConfirmDelete";
import { useEffect, useRef, useState } from "react";
import DraggableProjectListItem from "../DraggableProjectListItem/DraggableProjectListItem";
import "./AccountLevelItem.scss";
import { ReactComponent as FolderIcon} from "../../../../shared/icons/folder.svg";
import CustomIconButton from "../../../../shared/components/CustomIconButton/CustomIconButton";
import { SortOrder } from "../../../models/Enums";
import { sortProjects } from "../../../../shared/utils";

export type AccountLevelListProps = {
    item: DashboardItem;
    onEdit: (id: string, name: string) => void;
    onDelete: (id: string) => void;
    onProjectClick: (projectId:string) => void;
    onCopyProjectClick: (project:ProjectOverview) => void;
    onEditProjectClick: (project:ProjectOverview) => void;
    onDeleteProjectClick: (project:ProjectOverview) => void;
    sortOrder: SortOrder;
};

const AccountLevelItem = ({
    item,
    onEdit,
    onDelete,
    onProjectClick,
    onCopyProjectClick,
    onEditProjectClick,
    onDeleteProjectClick,
    sortOrder,
}: AccountLevelListProps) => {
    const intl = useIntl();
    const {isOver, setNodeRef} = useDroppable({
        id: item.id,
    })
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const togglerRef = useRef<HTMLDivElement>(null);
    const actionPopupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const togglerElement = togglerRef.current?.querySelector('button');
        const actionPopup = actionPopupRef.current;
        addListeners(togglerElement);
        addListeners(actionPopup);

        return () => {
            removeListeners(togglerElement);
            removeListeners(actionPopup);
        }
    }, []);

    const addListeners = (element: HTMLElement | null | undefined ) => {
        element?.addEventListener("mouseenter", ()=> {
            setIsHover(true);
        })
        element?.addEventListener("mouseleave", ()=> {
            setIsHover(false);
        })
    }
    const removeListeners = (element: HTMLElement | null | undefined ) => {
        element?.removeEventListener("mouseenter", ()=> {
            setIsHover(true);
        })
        element?.removeEventListener("mouseleave", ()=> {
            setIsHover(false);
        })
    }

    const handleDelete = (accountLevelId: string) => {
        setConfirmOpen(false);
        onDelete(accountLevelId);
    }

    const wrapperClasses = clsx("tcsp-account-level__item-wrapper", {"tcsp-account-level__item-wrapper-drop": isOver})
    const actionClasses = clsx("tcsp-project-list__item-actions", { "tcsp-project-list__item-actions-show" : isHover})

    const getIcon = () => {
        if (isHover && isExpanded) {
            return (<Icon iconId="angle-up" size={6}/>);
        }
        if (isHover && !isExpanded) {
            return (<Icon iconId="angle-down" size={6}/>);
        }
      return (<Box flexShrink={0}><FolderIcon height={24} width={24} /></Box>);
    }

    const handleExpandedChange = (expandedIds: string[]) => {
        if(expandedIds.length > 0) {
            setIsExpanded(true)
        } else {
            setIsExpanded(false);
        }
    }

    return (
        <Box ref={setNodeRef} component="li" className={wrapperClasses} ml={3} mr={5}>
            <Box ml={1}>
                <Accordion onExpandedChanged={handleExpandedChange}>
                        <AccordionItem ref={togglerRef} togglerContent={(
                            <Box className="tcsp-account-level__item" py={1} pr={3} alignItems="center" >
                                <Box container alignItems="center">
                                    { getIcon() }
                                    <Typography ml={6} variant="Body1" textAlign="left" text={`${item.name} (${item.projects.length})`} />
                                </Box>
                                <ProjectStatusTag projectStatus={item.status} />
                                <Typography variant="Body1" />
                                <Typography variant="Body1" />
                                <Typography variant="Body1" />
                                <LatestUpdateText updatedWhen={item.updatedWhen} />
                            </Box>
                        )}>
                        <Box ml={-14} mr={-5}>
                            {item.projects.length < 1 && (
                                <Box my={5}>
                                    <Typography ml={14} variant="Body2" text="Drag and drop a project here to add it to this account level." />
                                </Box>
                            )}
                            <ol className="tcsp-account-level__item-list">
                                {sortProjects(item.projects, sortOrder)?.map(project => (
                                    <DraggableProjectListItem
                                        isSubItem
                                        key={project.id + Date.now}
                                        project={project}
                                        onItemClick={() => onProjectClick(project.id)}
                                        onDuplicateItemClick={() => onCopyProjectClick(project)}
                                        onDeleteItemClick={() => onDeleteProjectClick(project)}
                                        onEditItemClick={() => onEditProjectClick(project)}/>
                                ))}
                            </ol>
                        </Box>
                        </AccordionItem>
                </Accordion>
            </Box>
            <Box
                ref={actionPopupRef}
                className={actionClasses}
                backgroundColor="White"
                br={3}
                flexDirection="row"
                gap={1} >
                <Tooltip toolTipText={intl.formatMessage({id: "project.itemAction.Edit.tooltip", defaultMessage: "Edit"})}>
                    <IconButton size="Medium" icon="pen" onClick={() => onEdit(item.id, item.name)} />
                </Tooltip>
                <Tooltip toolTipText={intl.formatMessage({id: "project.itemAction.ungroup.tooltip", defaultMessage: "Ungroup"})}>
                    <CustomIconButton size={4} icon="ungroup" onClick={() => setConfirmOpen(true)} />
                </Tooltip>
            </Box>
            <ConfirmDelete isOpen={confirmOpen}
                onConfirm={() => handleDelete(item.id)}
                onCancel={() => setConfirmOpen(false)}
                headingText={intl.formatMessage({id:"accountLevel.confirmDelete.title", defaultMessage:"You are about to delete a folder"})}
                bodyText={intl.formatMessage({id:"accountLevel.confirmDelete.message", defaultMessage:"None of the projects in the folder will be affected. Are you sure that you want to delete this folder permanently?"})}
                primaryButtonText={intl.formatMessage({id: "accountLevel.confirmDelete.confirmDelete", defaultMessage: "Delete folder"})}
                />
        </Box>
    );
}
export default AccountLevelItem;
